import { useCallback, useEffect, useReducer, useRef } from "react";

import { FeatureEnrollmentPageViewKind } from "@/pages/feature-enrollment/feature-enrollment.types";
import {
  acceptFeatureEnrollmentLinkBySlug,
  fetchFeatureEnrollmentLinkBySlug,
  getNormalizedFeatureEnrollmentSlug,
} from "@/pages/feature-enrollment/feature-enrollment.utils";
import type { FeatureEnrollmentLinkSummary } from "@/pages/feature-enrollment/feature-enrollment.utils";

type FeatureEnrollmentPageState = {
  kind: FeatureEnrollmentPageViewKind;
  link: FeatureEnrollmentLinkSummary | null;
  isAccepting: boolean;
  acceptError: string | null;
  statusTitle: string | null;
  statusBody: string | null;
};

type FeatureEnrollmentPageAction =
  | { type: "reset"; payload: FeatureEnrollmentPageState }
  | { type: "start-loading" }
  | { type: "set-ready"; link: FeatureEnrollmentLinkSummary }
  | { type: "set-already-enrolled"; link: FeatureEnrollmentLinkSummary }
  | {
      type: "set-status";
      kind: FeatureEnrollmentPageViewKind;
      title: string | null;
      body: string | null;
      link?: FeatureEnrollmentLinkSummary;
    }
  | { type: "accept-start" }
  | { type: "accept-error"; message: string };

const getInitialState = (slug?: string): FeatureEnrollmentPageState => {
  const normalizedSlug = getNormalizedFeatureEnrollmentSlug(slug);

  if (!normalizedSlug) {
    return {
      kind: FeatureEnrollmentPageViewKind.NotFound,
      link: null,
      isAccepting: false,
      acceptError: null,
      statusTitle: "We couldn't open this feature enrollment link.",
      statusBody: null,
    };
  }

  return {
    kind: FeatureEnrollmentPageViewKind.Loading,
    link: null,
    isAccepting: false,
    acceptError: null,
    statusTitle: null,
    statusBody: null,
  };
};

const reducer = (
  state: FeatureEnrollmentPageState,
  action: FeatureEnrollmentPageAction
): FeatureEnrollmentPageState => {
  switch (action.type) {
    case "reset":
      return action.payload;
    case "start-loading":
      return {
        ...state,
        kind: FeatureEnrollmentPageViewKind.Loading,
        link: null,
        isAccepting: false,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
      };
    case "set-ready":
      return {
        ...state,
        kind: FeatureEnrollmentPageViewKind.Ready,
        link: action.link,
        isAccepting: false,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
      };
    case "set-already-enrolled":
      return {
        ...state,
        kind: FeatureEnrollmentPageViewKind.AlreadyEnrolled,
        link: action.link,
        isAccepting: false,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
      };
    case "set-status":
      return {
        ...state,
        kind: action.kind,
        link: action.link ?? state.link,
        isAccepting: false,
        acceptError: null,
        statusTitle: action.title,
        statusBody: action.body,
      };
    case "accept-start":
      return {
        ...state,
        isAccepting: true,
        acceptError: null,
      };
    case "accept-error":
      return {
        ...state,
        isAccepting: false,
        acceptError: action.message,
      };
    default:
      return state;
  }
};

const getStatusCopy = ({
  type,
  title,
}: {
  type: FeatureEnrollmentPageViewKind;
  title?: string;
}): { title: string; body: string | null } => {
  switch (type) {
    case FeatureEnrollmentPageViewKind.Expired:
      return {
        title: title ?? "This feature enrollment link has expired.",
        body: "Ask the person who sent it for a new link.",
      };
    case FeatureEnrollmentPageViewKind.Disabled:
      return {
        title: title ?? "This feature enrollment link is no longer available.",
        body: "Ask the person who sent it for an updated link.",
      };
    case FeatureEnrollmentPageViewKind.NotFound:
      return {
        title: title ?? "We couldn't open this feature enrollment link.",
        body: "Check the link and try again.",
      };
    case FeatureEnrollmentPageViewKind.Error:
      return {
        title: title ?? "We couldn't process this feature enrollment link.",
        body: "Please refresh the page and try again.",
      };
    default:
      return {
        title: title ?? "We couldn't process this feature enrollment link.",
        body: "Please refresh the page and try again.",
      };
  }
};

interface UseFeatureEnrollmentPageStateArgs {
  slug?: string;
}

export const useFeatureEnrollmentPageState = ({ slug }: UseFeatureEnrollmentPageStateArgs) => {
  const [state, dispatch] = useReducer(reducer, getInitialState(slug));
  const normalizedSlug = getNormalizedFeatureEnrollmentSlug(slug);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "reset", payload: getInitialState(slug) });
  }, [slug]);

  useEffect(() => {
    if (!normalizedSlug) {
      return;
    }

    let isCancelled = false;

    dispatch({ type: "start-loading" });

    const fetchEnrollmentLink = async () => {
      const result = await fetchFeatureEnrollmentLinkBySlug(normalizedSlug);

      if (isCancelled || !isMountedRef.current) {
        return;
      }

      switch (result.kind) {
        case "ready":
          dispatch({ type: "set-ready", link: result.link });
          return;
        case "already-enrolled":
          dispatch({ type: "set-already-enrolled", link: result.link });
          return;
        case "expired": {
          const { title, body } = getStatusCopy({
            type: FeatureEnrollmentPageViewKind.Expired,
            title: result.message,
          });
          dispatch({
            type: "set-status",
            kind: FeatureEnrollmentPageViewKind.Expired,
            title,
            body,
            link: result.link,
          });
          return;
        }
        case "disabled": {
          const { title, body } = getStatusCopy({
            type: FeatureEnrollmentPageViewKind.Disabled,
            title: result.message,
          });
          dispatch({
            type: "set-status",
            kind: FeatureEnrollmentPageViewKind.Disabled,
            title,
            body,
            link: result.link,
          });
          return;
        }
        case "not-found": {
          const { title, body } = getStatusCopy({
            type: FeatureEnrollmentPageViewKind.NotFound,
            title: result.message,
          });
          dispatch({
            type: "set-status",
            kind: FeatureEnrollmentPageViewKind.NotFound,
            title,
            body,
          });
          return;
        }
        case "error": {
          const { title, body } = getStatusCopy({
            type: FeatureEnrollmentPageViewKind.Error,
            title: result.message,
          });
          dispatch({
            type: "set-status",
            kind: FeatureEnrollmentPageViewKind.Error,
            title,
            body,
          });
          return;
        }
        default:
          return;
      }
    };

    void fetchEnrollmentLink();

    return () => {
      isCancelled = true;
    };
  }, [normalizedSlug]);

  const handleAccept = useCallback(async (): Promise<FeatureEnrollmentLinkSummary | null> => {
    if (
      !normalizedSlug ||
      state.kind !== FeatureEnrollmentPageViewKind.Ready ||
      state.isAccepting
    ) {
      return null;
    }

    dispatch({ type: "accept-start" });

    const result = await acceptFeatureEnrollmentLinkBySlug(normalizedSlug);

    if (!isMountedRef.current) {
      return null;
    }

    switch (result.kind) {
      case "accepted":
        return result.link;
      case "expired": {
        const { title, body } = getStatusCopy({
          type: FeatureEnrollmentPageViewKind.Expired,
          title: result.message,
        });
        dispatch({
          type: "set-status",
          kind: FeatureEnrollmentPageViewKind.Expired,
          title,
          body,
        });
        return null;
      }
      case "disabled": {
        const { title, body } = getStatusCopy({
          type: FeatureEnrollmentPageViewKind.Disabled,
          title: result.message,
        });
        dispatch({
          type: "set-status",
          kind: FeatureEnrollmentPageViewKind.Disabled,
          title,
          body,
        });
        return null;
      }
      case "not-found": {
        const { title, body } = getStatusCopy({
          type: FeatureEnrollmentPageViewKind.NotFound,
          title: result.message,
        });
        dispatch({
          type: "set-status",
          kind: FeatureEnrollmentPageViewKind.NotFound,
          title,
          body,
        });
        return null;
      }
      case "error":
        dispatch({ type: "accept-error", message: result.message });
        return null;
      default:
        dispatch({ type: "accept-error", message: "Something went wrong. Please try again." });
        return null;
    }
  }, [normalizedSlug, state.isAccepting, state.kind]);

  return {
    ...state,
    handleAccept,
  };
};
